<template>
  <v-dialog :value="value" max-width="610" persistent>
    <v-snackbar
      top
      v-model="perishablesAlert"
      content-class="pa-0"
      color="warning"
      :timeout="-1">
      <v-alert
        v-model="perishablesAlert"
        dense
        class="my-0"
        border="left"
        prominent
        type="warning"
        :retain-focus="true">
        {{ perishablesAlertMessage }}
        <br/>
        <span>
          <v-btn
            outlined
            @click="submitPerishablesWithWarning"
            :loading="saving"
            class="mt-4 mr-4">
            Continue
          </v-btn>
          <v-btn
            class="mt-4"
            @click="perishablesAlert = false">
            Cancel
          </v-btn>
        </span>
      </v-alert>
    </v-snackbar>
    <v-snackbar
      top
      v-model="lateSubmissionAlert"
      content-class="pa-0"
      color="warning"
      :timeout="-1">
      <v-alert
        v-model="lateSubmissionAlert"
        dense
        class="my-0"
        border="left"
        prominent
        type="warning"
        :retain-focus="true">
        <div class="mb-1">
          {{ lateSubmissionMessage }}
        </div>
        <br/>
        Continue to submit promotion?
        <span>
          <v-btn
            outlined
            @click="submitLateSubmission"
            :loading="saving"
            class="mx-4">
            Yes
          </v-btn>
          <v-btn @click="lateSubmissionAlert = false">
            No
          </v-btn>
        </span>
      </v-alert>
    </v-snackbar>
    <v-card>
      <v-card-title>Update Promotion Status 
      <v-spacer />
      <v-btn :disabled="cancelDisabled" @click="$emit('close')" text color="grey darken-2">
        Cancel
      </v-btn>
      <v-btn
        :loading="saving || loadingItems"
        :disabled="changesDisabled"
        color="primary"
        @click="saveStatus">
        Save
      </v-btn>
      </v-card-title>
      <v-card-text class="mb-0 pb-0 px-4">
        <v-container class="mt-0 pt-0">
          <v-row>
            <v-col v-if="promoStatus">
              <v-select
                v-model="promoStatus.status"
                label="Status"
                :menu-props="{ offsetY: true }"
                :items="validStatuses"
                item-text="name"
                item-color="action"
                return-object
                hide-details="auto"
                :disabled="changesDisabled"
                outlined
                dense>
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="promoStatus.notes"
                label="Notes"
                :disabled="changesDisabled"
                outlined
                dense
                hide-details>
              </v-textarea>
            </v-col>
          </v-row>
          <StatusHistory :promo="promo" />
        </v-container>
      </v-card-text>
    </v-card>
    <PromoConflictModal
      v-if="promoConflictAlert"
      v-model="promoConflictAlert"
      :duplicatePromos="duplicatePromos"
      :overlappingPromos="overlappingPromos"
      @close="closeModals"
      @submitWithOverlap="submitWithOverlap"
    />
    <DsdValidationModal
      v-if="dsdValidationAlert"
      v-model="dsdValidationAlert"
      :message="dsdValidationMessage"
      @close="closeModals"
    />
    <AdFeeAlertModal
      v-if="adFeeAlert"
      v-model="adFeeAlert"
      :promo="promo"
      :allowContinue="allowAdFeeContinue"
      @close="closeModals"
      @submitWithAdFeeAlert="submitWithAdFeeAlert"
    />
    <NetUnitAllowanceAlertModal
      v-if="netUnitAllowanceAlert"
      v-model="netUnitAllowanceAlert"
      :promoItems="netUnitAllowanceViolations"
      @close="closeModals"
      @submitWithNetUnitAllowanceAlert="submitWithNetUnitAllowanceAlert"
    />
    <MissingLinkedItemsAlertModal
      v-if="missingLinkedItemsAlert"
      v-model="missingLinkedItemsAlert"
      :missingItems="missingLinkedItems"
      @close="closeModals"
      @submitWithMissingLinkedItemsAlert="submitWithMissingLinkedItemsAlert"
    />
    <ShortTPRAlertModal
      v-if="shortTPRAlert"
      v-model="shortTPRAlert"
      @close="closeModals"
      @submitWithShortTPRAlert="submitWithShortTPRAlert"
    />
    <TPRAlignmentAlertModal
      v-if="tprAlignmentAlert"
      v-model="tprAlignmentAlert"
      :misalignedPromos="misalignedPromos"
      :promo="promo"
      @close="closeModals"
      @submitMisalignedTPR="submitMisalignedTPR"
    />
  </v-dialog>
</template>
<script>
const PromoConflictModal = () => import("@/components/PromoConflictModal.vue")
const DsdValidationModal = () => import("@/components/DsdValidationModal.vue")
const ShortTPRAlertModal = () => import("@/components/ShortTPRAlertModal.vue")
const AdFeeAlertModal = () => import("@/components/AdFeeAlertModal.vue")
const NetUnitAllowanceAlertModal = () => import("@/components/NetUnitAllowanceAlertModal.vue")
const MissingLinkedItemsAlertModal = () => import("@/components/MissingLinkedItemsAlertModal.vue")
const TPRAlignmentAlertModal = () => import("@/components/TPRAlignmentAlertModal.vue")
import StatusHistory from "@/components/statusHistory.vue"
import WorkflowStatus from "@/axios/status-endpoints"
import Promos from '@/axios/promotion-endpoint'
import PromoStore from '@/axios/promotion-store-endpoint'
import PromoAllowance from '@/axios/promo-allowance-endpoints'
import { displayAlert } from "@/mixins/alert"
import { utils } from '@/mixins/utils'
import { userAccess } from '@/mixins/user-access'
import { formValidation } from "@/mixins/form-validation"
import { promoItemsProperties } from "@/mixins/promo-items"
export default {
  data() {
    return {
      saving: false,
      loading: false,
      missingLinkedItemsAlert: false,
      missingLinkedItemsMessage: '',
      lateSubmissionAlert: false,
      lateSubmissionMessage: '',
      dsdValidationAlert: false,
      dsdValidationMessage: '',
      promoConflictAlert: false,
      adFeeAlert: false,
      allowAdFeeContinue: true,
      netUnitAllowanceAlert: false,
      shortTPRAlert: false,
      skipOverlapCheck: false,
      skipLinkedItemsCheck: false,
      skipLateSubmissionCheck: false,
      skipAdFeeAlert: false,
      skipShortTPRCheck: false,
      perishablesAlert: false,
      perishablesAlertMessage: '',
      skipPerishablesCheck: false,
      tprAlignmentAlert: false,
      skipAlignmentCheck: false,
      promoStatus: null,
      promoHistories: {},
      workflowStatuses: [],
      validStatuses: [],
      duplicatePromos: [],
      overlappingPromos: [],
      misalignedPromos: [],
      netUnitAllowanceViolations: [],
      missingLinkedItems: [],
      argPerishableDepartments: [
        'Meat - Processed',
        'Meat - Frozen',
        'Meat - Fresh',
        'Meat - Seafood',
        'Deli',
        'Bakery',
        'Produce',
        'Produce - Floral']
    }
  },
  name: "StatusModal",
  mixins: [displayAlert, utils, formValidation, userAccess, promoItemsProperties],
  props: {
    value: Boolean,
    promo: Object,
    promoAllowances: {
      type: Array,
      default: () => ([])
    },
    promoPartyIds: {
      type: Array,
      default: () => ([])
    },
    vendorIds: {
      type: Array,
      default: () => ([])
    },
    promoItems: {
      type: Array,
      default: () => ([])
    },
    promoStores: {
      type: Array,
      default: () => ([])
    },
    loadingItems: Boolean,
    validLumpSums: Boolean,
    validSubvendorSelected: Boolean,
    validCategoryManagerSelected: Boolean,
    lumpSumTotal: Number,
    isPromoStatusAdmin: Boolean,
    readOnlyPromo: Boolean,
    promoChangesAreSaved: Boolean,
    totalItems: Number
  },
  components: { StatusHistory, PromoConflictModal, AdFeeAlertModal, DsdValidationModal, NetUnitAllowanceAlertModal, MissingLinkedItemsAlertModal, ShortTPRAlertModal, TPRAlignmentAlertModal },
  created () {
    if (this.$auth.tenant === 'alliance-retail-group') {
      if (this.promoItems.length !== this.totalItems) this.$emit('initPromoItems')
      if (this.promoAllowances.length === 0) this.initPromoAllowances()
    }
    if (this.promo?.status?.status) {
      this.promoStatus = JSON.parse(JSON.stringify(this.promo.status))
      this.validStatuses.push({ ...this.promoStatus.status })
    }
    this.initWorkflowStatuses()
  },
  computed: {
    invalidTotalErrorMessage () {
      if (['pricechopper', 'alliance-retail-group'].includes(this.$auth.tenant)){
          const message = `The ad fee on this promotion is $${this.promo.lump_sum_allowance} but the ad group ad fee total is $${this.lumpSumTotal.toFixed(2)}.
          Please assign the remaining $${this.lumpSumDiff} to continue.`
          return message
        } else {
          const message = `The lump sum allowance on this promotion is $${this.promo.lump_sum_allowance} but the ad group lump sum total is $${this.lumpSumTotal.toFixed(2)}.
          Please assign the remaining $${this.lumpSumDiff} to continue.`
          return message
        }
    },
    currentStatus () {
      if (this.workflowStatuses.length > 0) {
        return this.workflowStatuses.find(status => {
          return status.current_status.id === this.promo.status.status.id
        })
      }
      return null
    },
    adminOnlyStatuses () {
      return ['APPROVED', 'REJECTED', 'VENDOR_REVIEW', 'VENDOR_APPROVED']
    },
    nextStatuses () {
      let nextValues = this.currentStatus?.next_statuses || []
      if (!this.isPromoStatusAdmin) {
        nextValues = nextValues.filter(s => !this.adminOnlyStatuses.includes(s?.constant))
      }
      if (this.$auth.tenant === 'alliance-retail-group') {
        if (this.limitAccessByRelatedVendors) {
          nextValues = nextValues.filter(s => s.constant !== 'CHANGES_REQUESTED')
        }
        nextValues = nextValues.filter(s => s.constant !== 'VENDOR_APPROVED')
      }
      return nextValues
    },
    isAdminOnlyStatus () {
      return this.adminOnlyStatuses.includes(this.currentStatus?.current_status?.constant)
    },
    isSubmittingOrApproving () {
      return ['SUBMITTED', 'APPROVED'].includes(this.promoStatus?.status?.constant)
    },
    autoApprove () {
      const categoryName = this.promo?.promo_category?.name.toLowerCase()
      return this.$auth.tenant === 'pricechopper' && (categoryName.includes('edlc') || categoryName.includes('tpr')) && this.promoStatus.status?.constant === 'SUBMITTED'
    },
    changesDisabled () {
      return this.saving
        || (this.nextStatuses.length === 0)
        || ((this.isAdminOnlyStatus || this.readOnlyPromo) && !this.isPromoStatusAdmin)
    },
    cancelDisabled () {
      return this.saving
    },
    promoLumpSum () {
      return this.getNumericPrice(this.promo.lump_sum_allowance)
    },
    checkLumpSums () {
      if (this.$auth.tenant === 'pricechopper') {
        return (this.promoLumpSum > 0) && ['APPROVED'].includes(this.promoStatus?.status?.constant)
      }
      if (this.$auth.tenant === 'alliance-retail-group' && this.promo.merged_from_promo_ids) {
        return false
      }
      return (this.promoLumpSum > 0) && this.isSubmittingOrApproving
    },
    lumpSumDiff () {
      const diff = (this.promoLumpSum - this.lumpSumTotal)
      return diff.toFixed(2)
    },
    checkSubvendors () {
      if (['alliance-retail-group','pricechopper'].includes(this.$auth.tenant)) {
        return ['SUBMITTED', 'APPROVED'].includes(this.promoStatus?.status?.constant) && (!['AMAP', 'Early Buy Allowance'].includes(this.promo.promo_category.name))
      }
      return false
    },
    checkCategoryManagers () {
      if (['alliance-retail-group','pricechopper'].includes(this.$auth.tenant)) {
        return ['SUBMITTED', 'APPROVED'].includes(this.promoStatus?.status?.constant)
      }
      return false
    },
    checkLinkedItems () {
      if (this.$auth.tenant === 'alliance-retail-group') {
        if (this.promo.merged_from_promo_ids) {
          return false
        }
        return this.isSubmittingOrApproving && !this.skipLinkedItemsCheck
      }
      return (this.$auth.tenant === 'pricechopper') && this.promoStatus.status.constant === 'SUBMITTED' && !this.skipLinkedItemsCheck
    },
    shouldSkipChecks () {
      return this.$auth.tenant !== 'alliance-retail-group' || this.promo.merged_from_promo_ids
    },
    checkLateSubmission () {
      if (this.shouldSkipChecks) return false
      return this.isSubmittingOrApproving && !this.skipLateSubmissionCheck && (this.$auth.tenant === 'alliance-retail-group' ? !this.isNonStandardCategory : true)
    },
    checkDuplicatePromos () {
      if (this.shouldSkipChecks) return false
      return this.isSubmittingOrApproving
    },
    checkOverlappingPromos () {
      if (this.shouldSkipChecks || 
        (this.$auth.tenant === 'alliance-retail-group' && this.isNonAllowanceCategory(this.promo?.promo_category?.name))
        ) return false
      return this.isSubmittingOrApproving && !this.skipOverlapCheck
    },
    checkPromoAlignment () {
      if (this.shouldSkipChecks || (this.$auth.tenant === 'alliance-retail-group' && this.isNonAllowanceCategory(this.promo?.promo_category?.name))) {
        return false
      }
      return ['TPR', 'EDLC'].includes(this.promo.billing_mode.constant) 
        && this.isSubmittingOrApproving
        && !this.skipAlignmentCheck
    },
    checkDsdRules () {
      if (this.shouldSkipChecks) return false
      return this.isSubmittingOrApproving && (this.dsdItemIds.length > 0 || this.promo?.dsd)
    },
    checkVendorItemAccess () {
      return (this.$auth.tenant === 'alliance-retail-group') && !this.isPromoVendorAdmin
    },
    dsdItemIds () {
      return this.promoItems.map(pi => pi.item.item_id).filter(id => /^[a-z]/i.test(id))
    },
    autoAssignAdFee () {
      return (this.$auth.tenant === 'alliance-retail-group') 
        && this.checkLumpSums && !this.validLumpSums 
        && !(this.isPromoStatusAdmin && this.isNonAllowanceCategory(this.promo?.promo_category?.name))
    },
    checkAdNonAllowanceAdFee () {
      return this.$auth.tenant === 'alliance-retail-group' 
        && this.checkLumpSums 
        && ['Ad Scan Non-Allowance', 'Ad Non Allowance'].includes(this.promo?.promo_category?.name)
    },
    checkNetUnitAllowances () {
      if (this.shouldSkipChecks) return false
      const isNotDSD = this.promo?.promo_category?.name.toLowerCase() !== 'dsd off invoice'
      const allowanceBillingMode = ['AD', 'TPR', 'EDLC'].includes(this.promo.billing_mode.constant)
      return !this.isComplexPromo && this.isSubmittingOrApproving && isNotDSD &&
      (this.$auth.tenant === 'alliance-retail-group' ? !this.isNonStandardCategory : true) && 
      allowanceBillingMode && !this.isNonAllowanceCategory(this.promo?.promo_category?.name)
    },
    checkShortTPRLength () {
      if (this.shouldSkipChecks) return false
      return this.isSubmittingOrApproving && 
             this.promo?.billing_mode?.constant === 'TPR' && 
             !this.skipShortTPRCheck
    },
    isNonStandardCategory () {
      return ['ad billback - non standard', 'pcaf - non standard'].includes(this.promo?.promo_category?.name.toLowerCase())
    },
    anyStoresOnPromo() {
      return this.promoStores?.some(party => party?.party_type_name?.toLowerCase() === 'store')
    },
    anyAdGroupsOnPromo() {
      return this.promoStores?.some(party => party?.party_type_name?.toLowerCase() === 'ad group')
    },
    checkPerishables() {
      return this.$auth.tenant === 'alliance-retail-group' && ['SUBMITTED', 'APPROVED'].includes(this.promoStatus?.status?.constant) && (this.promo.perishable || this.containsPerishables)
    },
    containsPerishables() {
      return this.promoItems.some(item => this.argPerishableDepartments.includes(item.item.department))
    }
  },
  methods: {
    async initWorkflowStatuses () {
      const workflowId = this.promo.status.workflow_id
      try {
        const res = await WorkflowStatus.getWorkflowValues(workflowId)
        this.workflowStatuses = res?.data || []
        if (this.nextStatuses.length > 0) {
          this.validStatuses = [...this.validStatuses, ...this.nextStatuses].sort(this.sortByDisplayOrder)
        }
      } catch (err) {
        this.handleError(err)
      }
    },
    async saveStatus () {
      if (!this.promoChangesAreSaved) {
        const message = 'You must save all changes before updating promotion status'
        return this.emitAlert(true, 'warning', message)
      }

      if (this.$auth.tenant === 'alliance-retail-group' && this.checkLumpSums) {
        if (this.limitAccessByRelatedVendors && this.anyStoresOnPromo) {
          const message = 'Vendors are not allowed to load store-specific ad fees. Please reach out to your ARG category manager for assistance.'
          return this.emitAlert(true, 'warning', message)
        }
        if (this.anyStoresOnPromo && this.anyAdGroupsOnPromo) {
          const message ='A promo with an ad/TPR fee cannot have both stores and ad groups on it. Please navigate to the group/store tab and adjust accordingly.'
          return this.emitAlert(true, 'warning', message)
        }
        if (this.checkAdNonAllowanceAdFee && !this.skipAdFeeAlert && !(this.anyStoresOnPromo && this.validLumpSums)) {
          if (this.anyStoresOnPromo && !this.validLumpSums) {
            // store-specific ad fees can't be auto-assigned
            this.allowAdFeeContinue = false
          }
          return this.adFeeAlert = true
        }
      }

      if (this.checkLumpSums && !this.validLumpSums) {
        if (this.autoAssignAdFee || this.skipAdFeeAlert) {
          await this.assignAdFee()
          this.$emit('refreshStores')
        } else {
          const message = this.invalidTotalErrorMessage
          return this.emitAlert(true, 'warning', message)
        }
      }
      if (this.checkSubvendors && !this.validSubvendorSelected) {
        const message = 'At least one subvendor must be selected before the status can be changed.'
        return this.emitAlert(true, 'warning', message)
      }
      if (this.checkCategoryManagers && !this.validCategoryManagerSelected) {
        const message = 'At least one category manager must be selected before the status can be changed to "Submitted" or "Approved".'
        return this.emitAlert(true, 'warning', message)
      }

      if (this.checkShortTPRLength) {
        const validTPRLength = this.verifyTPRLength()
        if (!validTPRLength) {
          this.shortTPRAlert = true
          this.saving = false
          return
        }
      }
      if (this.checkLinkedItems) {
        this.saving = true
        const { data } = await Promos.checkLinkedItems(this.promo.id)
        this.missingLinkedItems  = this.vendorIds.length > 0 ? data.filter(item => { return this.vendorIds.includes(item?.vendor_id) }) : data
        if (this.missingLinkedItems.length > 0 && !this.skipLinkedItemsCheck) {
          this.missingLinkedItemsAlert = true
          this.saving = false
          return
        }
      }
      if (this.checkLateSubmission) {
        const maxDays = ['EDLC', 'TPR'].includes(this.promo.billing_mode.constant) || (this.promo.perishable && this.promo.billing_mode.constant === 'AD') ? 28 : 56
        let late = false
        const currentDate = new Date()
        if (this.promo.start_dt) {
          const startDate = new Date(this.promo.start_dt)
          const diff = Math.abs((startDate - currentDate) / (1000 * 3600 * 24))
          if (diff < maxDays) late = true
        }
        if (late) { 
          this.lateSubmissionMessage = `Start date entered is less than ${maxDays / 7} weeks from current date. Please reach out to ARG Category Manager for review and approval.`
          this.lateSubmissionAlert = true
          this.saving = false
          return
        }
      }
      if (this.checkDuplicatePromos) {
        const duplicates = await this.checkDuplicates()
        if (duplicates) return
      }
      if (this.checkOverlappingPromos) {
        const overlap = await this.checkOverlap()
        if (overlap) return
      }
      if (this.checkPromoAlignment) {
        const misalignedPromos = await this.checkTPRAlignment()
        if (misalignedPromos) return
      }
      if (this.checkDsdRules) {
        const dsdRuleBroken = this.checkDsdViolation()
        if (dsdRuleBroken) return
      }
      if (this.checkNetUnitAllowances && !this.skipNetUnitAllowanceAlert) {
        this.netUnitAllowanceViolations = this.checkNetUnitAllowanceViolations()
        if (this.netUnitAllowanceViolations.length > 0) { 
          this.netUnitAllowanceAlert = true
          return
        }
      }
      if (this.checkPerishables && !this.skipPerishablesCheck) {
        const notCheckedPerishable = this.containsPerishables && !this.promo.perishable
        const noPerishableItems = !this.containsPerishables && this.promo.perishable
        if (notCheckedPerishable) this.perishablesAlertMessage = 'This promotion has items included from a perishable department but the promotion is not flagged perishable. Please cancel and select the perishable flag if applicable. Please ensure your category manager selection is correct as well.'
        if (noPerishableItems) this.perishablesAlertMessage = 'This promotion is flagged perishable but does not have any items from a perishable department included. Please cancel and de-select the perishable flag if applicable. Please ensure your category manager selection is correct as well.'
        if (notCheckedPerishable || noPerishableItems) {
          this.perishablesAlert = true
          return
        }
      }
      this.saving = true
      try {
        const res = await WorkflowStatus.add(this.promoStatus)
        if (res?.data?.id && !this.autoApprove) {
          this.emitAlert(true, "success", "Promotion status updated successfully", [], true)
          this.$emit("saved")
        }
        if (this.autoApprove) {
          this.promoStatus.status = this.workflowStatuses.find(status => status.current_status.constant === 'APPROVED').current_status
          const approvedRes = await WorkflowStatus.add(this.promoStatus)
          if(approvedRes?.data?.id) {
            this.emitAlert(true, "success", "Promotion status updated successfully", [], true)
            this.$emit("saved")
          }
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.saving = false
      }
    },
    async assignAdFee () {
      this.loading = true
      try {
        const { data } = await PromoStore.getStores(this.promo.id, 250, 0)
        if (data?.length > 0) {
          const firstAdGroup = data.find(store => store.party_type_name === 'Ad Group')
          const payload = {
            promo_id: this.promo.id,
            party_id: firstAdGroup.store_id,
            allowance: this.lumpSumDiff
          }
          const allowancesRes = await PromoAllowance.getPromoAllowances(this.promo.id)
          const promoAllowances = allowancesRes?.data || []
          const existingAllowance = promoAllowances.find(p => p?.party_id === firstAdGroup.store_id)
          const promise = existingAllowance
            ? PromoAllowance.put(payload)
            : PromoAllowance.post(payload)
          await promise
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },
    closeModals () {
      this.promoConflictAlert = false
      this.adFeeAlert = false
      this.shortTPRAlert = false
      this.tprAlignmentAlert = false
      this.$emit('close')
    },
    sortByDisplayOrder (a, b) {
      return a.display_order - b.display_order
    },
    submitWithMissingLinkedItems () {
      this.missingLinkedItemsAlert = false
      this.skipLinkedItemsCheck = true
      this.saveStatus()
    },
    submitWithAdFeeAlert () {
      this.adFeeAlert = false
      this.skipAdFeeAlert = true
      this.allowAdFeeContinue = true
      this.saveStatus()
    },
    submitWithMissingLinkedItemsAlert () {
      this.missingLinkedItemsAlert = false
      this.skipLinkedItemsCheck = true
      this.saveStatus()
    },
    submitWithNetUnitAllowanceAlert () {
      this.netUnitAllowanceAlert = false
      this.skipNetUnitAllowanceAlert = true
      this.saveStatus()
    },
    submitWithShortTPRAlert () {
      this.shortTPRAlert = false
      this.skipShortTPRCheck = true
      this.saveStatus()
    },
    submitMisalignedTPR () {
      this.tprAlignmentAlert = false
      this.skipAlignmentCheck = true
      this.saveStatus()
    },
    verifyTPRLength () {
      let valid = true
      if (this.promo.start_dt) {
        const startDate = new Date(this.promo.start_dt)
        const endDate = new Date(this.promo.end_dt)
        const diff = Math.abs((startDate - endDate) / (1000 * 3600 * 24)) + 1
        if (diff < 21) valid = false
      }
      return valid
    },
    async checkOverlap() {
      try {
        this.saving = true
        const { data } = await Promos.checkOverlap(this.promo.id)
        if (data?.length > 0) {
          this.overlappingPromos = data
          this.promoConflictAlert = true
          return true
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.saving = false
      }
      return false
    },
    async checkDuplicates() {
      try {
        this.saving = true
        const { data } = await Promos.checkDupes(this.promo.id)
        if (data?.length > 0) {
          this.duplicatePromos = data
          this.promoConflictAlert = true
          return true
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.saving = false
      }
      return false
    },
    async checkTPRAlignment() {
      try {
        this.saving = true
        const { data } = await Promos.checkTPRAlignment(this.promo.id)
        if (data?.length > 0) {
          this.misalignedPromos = data
          this.tprAlignmentAlert = true
          return true
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.saving = false
      }
      return false
    },
    checkDsdViolation () {
      let violation = false
      try {
        this.saving = true
        this.dsdValidationMessage = ''
        if (this.dsdItemIds.length > 0 && !this.promo?.dsd) {
          this.dsdValidationMessage = 'DSD items can only be added to DSD promotions.'
          violation = true
        }
        if (this.promo?.dsd && this.dsdItemIds.length === 0) {
          this.dsdValidationMessage = 'DSD promotions must have at least one DSD item.'
          violation = true
        }
        if (this.promo?.promo_category?.name.toLowerCase().includes('dsd') && this.dsdItemIds.length !== this.promoItems.length) {
          this.dsdValidationMessage = 'Promotions with the DSD Off Invoice category can ONLY have DSD items.'
          violation = true
        }
        if (violation) {
          this.dsdValidationAlert = true
          return true
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.saving = false
      }
      return false
    },
    submitWithOverlap () {
      this.promoConflictAlert = false
      this.skipOverlapCheck = true
      this.saveStatus()
    },
    submitLateSubmission () {
      this.lateSubmissionAlert = false
      this.skipLateSubmissionCheck = true
      this.saveStatus()
    },
    submitPerishablesWithWarning () {
      this.perishablesAlert = false
      this.skipPerishablesCheck = true
      this.perishablesAlertMessage = ''
      this.saveStatus()
    },
    async initPromoAllowances () {
      const res = await PromoAllowance.getPromoAllowances(this.promo.id)
      this.$emit('setPromoAllowances', res?.data || [])
    },
    checkNetUnitAllowanceViolations () {
      const calculatedPromoItems = this.promoItems.map(pi => this.formatPromoItem(pi))
      return calculatedPromoItems.filter(pi => pi.net_unit_allowance < 0.1)
    }
  }
}
</script>
